import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Button from '../TailwindComponents/Button';
import Input from '../TailwindComponents/Input';
import Textarea from '../TailwindComponents/Textarea';
import Dropdown, { DropdownOption } from '../TailwindComponents/Dropdown';
import FileUploader from '../TailwindComponents/FileUploader';
import DateTimePicker from '../TailwindComponents/DatePicker';
import CollaboratorSelector, { Collaborator } from '../CollaboratorSelector';
import FormErrors from './FormErrors';
import { FormSchema, FormValidator } from '../../../utils/validation/form-schema';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import { useAppDispatch } from '../../../store';
import { MdCheckCircle } from 'react-icons/md';
import LogoSpinner from '../LogoSpinner';
import 'react-datepicker/dist/react-datepicker.css';

export type FieldType = 'input' | 'textarea' | 'dropdown' | 'fileUploader' | 'datePicker' | 'collaboratorSelector' | 'custom';

export interface BaseFormFieldConfig {
  name: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  required?: boolean;
  helpText?: string;
  description?: string;
  disabled?: boolean;
  gridSize?: 'full' | 'half' | 'third';
  inputType?: string;
  options?: DropdownOption[];
  rows?: number;
  allowedExtensions?: string[];
  maxFileSize?: number;
  maxFiles?: number;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  customComponent?: React.ComponentType<any>;
  customProps?: Record<string, any>;
  hidden?: boolean;
}

export interface BaseFormSectionConfig {
  title?: string;
  description?: string;
  fields: BaseFormFieldConfig[];
  gridCols?: number;
  /** Optional data-tour-id for onboarding tour targeting. */
  dataTourId?: string;
}

export interface BaseFormProps {
  title: string;
  description?: string;
  sections: BaseFormSectionConfig[] | BaseFormFieldConfig[];
  schema: FormSchema;
  initialValues?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => Promise<void> | void;
  onSecondarySubmit?: (data: Record<string, any>) => Promise<void> | void;
  onCancel?: () => void;
  submitLabel?: string;
  secondarySubmitLabel?: string;
  /** Independently disable the secondary submit button (e.g. when a prerequisite is missing). */
  secondarySubmitDisabled?: boolean;
  /** Tooltip shown when the secondary submit button is disabled via secondarySubmitDisabled. */
  secondarySubmitTooltip?: string;
  /** Independently disable the primary submit button (e.g. when a feature is locked). */
  submitDisabled?: boolean;
  /** Tooltip shown when the primary submit button is disabled via submitDisabled. */
  submitTooltip?: string;
  cancelLabel?: string;
  /** Optional back button (e.g. for wizard navigation). Renders on the left side of the footer. */
  onBack?: () => void;
  backLabel?: string;
  showConfirmationModal?: boolean;
  isSubmitting?: boolean;
  hideActions?: boolean;
  hideLoadingOverlay?: boolean;
  hideHeader?: boolean;
  className?: string;
  maxWidth?: string;
  loadingText?: string;
  progressSteps?: Array<{ name: string; status?: 'pending' | 'processing' | 'completed' | 'failed'; message?: string }>;
  isConnecting?: boolean;
  onValuesChange?: (values: Record<string, any>, changedField: string) => void;
}

const isSection = (item: any): item is BaseFormSectionConfig => {
  return 'fields' in item && Array.isArray(item.fields);
};

const BaseForm: React.FC<BaseFormProps> = ({
  title,
  description,
  sections,
  schema,
  initialValues = {},
  onSubmit,
  onSecondarySubmit,
  onCancel,
  submitLabel,
  secondarySubmitLabel,
  secondarySubmitDisabled,
  secondarySubmitTooltip,
  submitDisabled,
  submitTooltip,
  cancelLabel,
  onBack,
  backLabel,
  showConfirmationModal = true,
  isSubmitting = false,
  hideActions = false,
  hideLoadingOverlay = false,
  hideHeader = false,
  className,
  maxWidth = 'max-w-3xl',
  loadingText,
  progressSteps,
  isConnecting = false,
  onValuesChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const modalState = useSelector((state: RootState) => state.modal);
  const [formValues, setFormValues] = useState<Record<string, any>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [isDirty, setIsDirty] = useState(false);

  // Track which fields the user has explicitly modified — never reset these
  const dirtyFieldsRef = useRef<Set<string>>(new Set());

  // Sync formValues when initialValues change externally (e.g. collaborators from parent folder, org-wide toggle)
  // Only fires when initialValues reference changes — NOT when isDirty changes.
  // Fields the user has explicitly modified are never overwritten.
  useEffect(() => {
    setFormValues((prev) => {
      const updated = { ...prev };
      let changed = false;
      for (const key of Object.keys(initialValues)) {
        // Never reset fields the user has explicitly changed
        if (dirtyFieldsRef.current.has(key)) continue;

        if (JSON.stringify(prev[key]) !== JSON.stringify(initialValues[key])) {
          updated[key] = initialValues[key];
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  }, [initialValues]);
  const [localIsSubmitting, setLocalIsSubmitting] = useState(false);

  const formattedSections: BaseFormSectionConfig[] = useMemo(() => {
    if (sections.length === 0) return [];
    if (isSection(sections[0])) {
      return sections as BaseFormSectionConfig[];
    }
    return [{ fields: sections as BaseFormFieldConfig[] }];
  }, [sections]);

  const validateForm = useCallback(() => {
    const validator = new FormValidator(schema);
    const validation = validator.validate(formValues, false);
    setErrors(validation.errors);
    return validation.isValid;
  }, [formValues, schema]);

  const isFormValid = useMemo(() => {
    const validator = new FormValidator(schema);
    const validation = validator.validate(formValues, false);
    return validation.isValid;
  }, [formValues, schema]);

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    dirtyFieldsRef.current.add(fieldName);
    setFormValues((prev) => {
      const updated = { ...prev, [fieldName]: value };
      onValuesChange?.(updated, fieldName);
      return updated;
    });
    setIsDirty(true);

    const field = schema.getField(fieldName);
    if (field) {
      const result = field.validate(value);
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (result.isValid) {
          delete newErrors[fieldName];
        } else {
          newErrors[fieldName] = [result.error || 'Invalid value'];
        }
        return newErrors;
      });
    }
  }, [schema, onValuesChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLocalIsSubmitting(true);
    try {
      await onSubmit(formValues);
    } finally {
      setLocalIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isDirty && showConfirmationModal) {
      dispatch(
        showModal({
          showModal: true,
          type: 'confirmation',
          message: t('Discard Changes'),
          subMsg: t('You have unsaved changes. Are you sure you want to discard them?'),
          onConfirm: () => {
            dispatch(hideModal());
            onCancel?.();
          },
          onCancel: () => {
            dispatch(hideModal());
          },
        } as any)
      );
    } else {
      onCancel?.();
    }
  };

  const renderField = (field: BaseFormFieldConfig) => {
    if (field.hidden) return null;

    const gridClass = {
      full: 'col-span-full',
      half: 'col-span-1 md:col-span-1 lg:col-span-1',
      third: 'col-span-1 md:col-span-1 lg:col-span-1',
    }[field.gridSize || 'full'];

    const fieldError = errors[field.name]?.[0];
    const isDisabled = field.disabled || localIsSubmitting || isSubmitting;

    return (
      <div key={field.name} className={gridClass} data-tour-id={`field-${field.name}`}>
        {field.type === 'input' && (
          <Input
            id={field.name}
            name={field.name}
            label={field.label}
            placeholder={field.placeholder}
            type={field.inputType || 'text'}
            value={formValues[field.name] ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            error={fieldError ? true : false}
            helperText={fieldError}
            disabled={isDisabled}
            required={field.required}
            description={field.description}
          />
        )}

        {field.type === 'textarea' && (
          <Textarea
            id={field.name}
            name={field.name}
            label={field.label}
            placeholder={field.placeholder}
            value={formValues[field.name] ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            error={fieldError}
            disabled={isDisabled}
            required={field.required}
            description={field.description}
            rows={field.rows || 4}
          />
        )}

        {field.type === 'dropdown' && (
          <Dropdown
            label={field.label}
            options={field.options || []}
            value={formValues[field.name] ?? (field.customProps?.multiple ? [] : '')}
            onChange={(value) => handleFieldChange(field.name, value)}
            placeholder={field.placeholder}
            useApi={false}
            required={field.required}
            {...field.customProps}
          />
        )}

        {field.type === 'fileUploader' && (
          <div className="flex flex-col">
            {field.label && (
              <label className="block text-sm text-gray-700 dark:text-gray-200 mb-2">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            <FileUploader
              files={formValues[field.name] || []}
              onFilesChange={(files) => handleFieldChange(field.name, files)}
              allowedExtensions={field.allowedExtensions}
              maxSizeBytes={field.maxFileSize || 52428800}
              maxFiles={field.maxFiles}
            />
            {fieldError && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{fieldError}</p>
            )}
          </div>
        )}

        {field.type === 'datePicker' && (
          <DateTimePicker
            id={field.name}
            label={field.label}
            selected={formValues[field.name] ? new Date(formValues[field.name]) : null}
            onChange={(date) => handleFieldChange(field.name, date?.toISOString() || '')}
            placeholder={field.placeholder}
            minDate={field.minDate}
            maxDate={field.maxDate}
            disabled={isDisabled}
            dateFormat={field.dateFormat || 'MMM d, yyyy'}
            showTimeSelect={true}
            required={field.required}
            error={fieldError ? true : false}
            helperText={fieldError}
            fullWidth={field.customProps?.fullWidth || false}
          />
        )}

        {field.type === 'collaboratorSelector' && (
          <CollaboratorSelector
            collaborators={formValues[field.name] || []}
            onCollaboratorsChange={(collaborators: Collaborator[]) => handleFieldChange(field.name, collaborators)}
            title={field.label}
            description={field.description}
            {...field.customProps}
          />
        )}

        {field.type === 'custom' && field.customComponent && (
          <field.customComponent
            id={field.name}
            name={field.name}
            label={field.label}
            value={formValues[field.name]}
            onChange={(value: any) => handleFieldChange(field.name, value)}
            disabled={isDisabled}
            required={field.required}
            {...field.customProps}
          />
        )}
      </div>
    );
  };

  const totalErrors = Object.keys(errors).length;

  return (
    <form onSubmit={handleSubmit} className={clsx('flex flex-col h-full relative', className)}>
      {/* Submission Loading Overlay */}
      {(localIsSubmitting || isSubmitting) && !hideLoadingOverlay && (
        <div className="absolute inset-0 bg-black bg-opacity-40 dark:bg-opacity-60 flex items-center justify-center z-50 rounded-lg">
          {progressSteps && progressSteps.length > 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-xl flex flex-col items-center gap-4 max-w-sm max-h-96 overflow-y-auto">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {loadingText || t('Processing...')}
              </h3>
              <div className="w-full space-y-2">
                {progressSteps.map((step, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      {step.status === 'completed' ? (
                        <MdCheckCircle className="h-5 w-5 text-green-600" />
                      ) : step.status === 'processing' ? (
                        <div className="h-5 w-5 rounded-full border-2 border-primary-500 border-t-primary-600 animate-spin" />
                      ) : step.status === 'failed' ? (
                        <div className="h-5 w-5 text-red-600">✕</div>
                      ) : (
                        <div className="h-5 w-5 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-900 dark:text-gray-100 truncate">
                        {step.name}
                      </p>
                      {step.message && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {step.message}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <LogoSpinner size="large" />
          )}
        </div>
      )}

      <div className="flex-1 !overflow-y-auto custom-scrollbar-main min-h-0">
        <div className={clsx('mx-auto w-full', maxWidth)}>
          {/* Header */}
          {!hideHeader && (
            <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl text-gray-900 dark:text-gray-100 mb-1">
                {title}
              </h2>
              {description && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {description}
                </p>
              )}
            </div>
          )}

          {/* Form Errors Summary */}
          {/* {totalErrors > 0 && (
            <div className="px-6 pt-4">
              <FormErrors
                errors={errors}
                isValid={false}
              />
            </div>
          )} */}

          {/* Sections */}
          <div className="px-6 py-6 space-y-8">
            {formattedSections.map((section, sectionIdx) => (
              <div key={sectionIdx} data-tour-id={section.dataTourId}>
                {section.title && (
                  <div className="mb-4">
                    <h3 className="text-base text-gray-900 dark:text-gray-100">
                      {section.title}
                    </h3>
                    {section.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {section.description}
                      </p>
                    )}
                  </div>
                )}

                <div
                  className={clsx(
                    'grid gap-4',
                    `grid-cols-${section.gridCols || 1}`
                  )}
                  style={{
                    gridTemplateColumns:
                      section.gridCols === 1
                        ? '1fr'
                        : section.gridCols === 2
                          ? 'repeat(2, 1fr)'
                          : section.gridCols === 3
                            ? 'repeat(3, 1fr)'
                            : 'repeat(auto-fit, minmax(250px, 1fr))',
                  }}
                >
                  {section.fields.map((field) => renderField(field))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-6 py-4">
        <div className={clsx('mx-auto w-full flex gap-3', onBack ? 'justify-between' : 'justify-end', maxWidth)}>
          {onBack && !hideActions && (
            <Button
              variant="neutral"
              size="small"
              onClick={onBack}
              disabled={localIsSubmitting || isSubmitting}
            >
              {backLabel || t('Back')}
            </Button>
          )}
          <div className="flex gap-3">
          {onCancel && !hideActions && (
          <Button
            variant="neutral"
            size="small"
            onClick={handleCancel}
            disabled={localIsSubmitting || isSubmitting}
          >
            {cancelLabel || t('Cancel')}
          </Button>
        )}
        {!hideActions && onSecondarySubmit && (
        <Button
          variant="neutral"
          size="small"
          type="button"
          disabled={!isFormValid || localIsSubmitting || isSubmitting || secondarySubmitDisabled}
          isLoading={localIsSubmitting || isSubmitting}
          tooltip={secondarySubmitDisabled ? secondarySubmitTooltip : undefined}
          onClick={async (e: React.MouseEvent) => {
            e.preventDefault();
            if (isFormValid && !secondarySubmitDisabled && onSecondarySubmit) {
              await onSecondarySubmit(formValues);
            }
          }}
        >
          {secondarySubmitLabel || t('Submit & Process')}
        </Button>
        )}
        {!hideActions && (
        <Button
          variant="primary"
          size="small"
          type="submit"
          disabled={!isFormValid || localIsSubmitting || isSubmitting || submitDisabled}
          isLoading={localIsSubmitting || isSubmitting}
          tooltip={submitDisabled ? submitTooltip : undefined}
        >
          {submitLabel || t('Submit')}
        </Button>
        )}
        </div>
        </div>
      </div>
    </form>
  );
};

export default BaseForm;
